/**
 * Cursor Behavior
 *
 * Maps ViewState to CSS cursor on the container element.
 * Also sets cursor on hovered node elements (to override child cursors).
 * Supports tool-specific SVG cursors via setToolCursor().
 */

import type { ViewState, ResizeHandlePosition } from "../types";
import {
	RESIZE_CURSORS,
	getRotatedResizeCursor,
	parseResizeHandleId,
} from "../types";
import type { DirectCanvas } from "@rool/renderer";

// =============================================================================
// Types
// =============================================================================

export interface CursorBehaviorContext {
	container: HTMLElement;
	canvas: DirectCanvas | null;
	getNodeRotation?: (nodeId: string) => number;
}

export interface CursorManager {
	/**
	 * Update cursor based on current ViewState.
	 * Call this on every ViewState change.
	 */
	update(state: ViewState): void;

	/**
	 * Set a tool cursor (e.g., SVG data URI from NodeTool.cursorSvg).
	 * Pass null to clear the tool cursor.
	 */
	setToolCursor(cursor: string | null): void;

	/** Reset cursor to default */
	reset(): void;
}

// =============================================================================
// Implementation
// =============================================================================

export function createCursorManager(ctx: CursorBehaviorContext): CursorManager {
	const { container, canvas, getNodeRotation } = ctx;
	let lastCursorNodeId: string | null = null;
	let toolCursor: string | null = null;

	function clearNodeCursor(): void {
		if (lastCursorNodeId && canvas) {
			const element = canvas.getNodeElement(lastCursorNodeId);
			if (element) {
				element.style.cursor = "";
			}
		}
		lastCursorNodeId = null;
	}

	return {
		update(state: ViewState): void {
			// During active operations, set specific cursors
			if (state.resize) {
				// During resize: use rotated cursor for the active handle
				const rotation = state.resize.rotation;
				const cursor = getRotatedResizeCursor(state.resize.handle, rotation);
				container.style.cursor = cursor;
				clearNodeCursor();
				return;
			}

			if (state.rotate) {
				container.style.cursor = "grabbing";
				clearNodeCursor();
				return;
			}

			if (state.drag) {
				container.style.cursor = "grabbing";
				clearNodeCursor();
				return;
			}

			if (state.connecting) {
				container.style.cursor = "crosshair";
				clearNodeCursor();
				return;
			}

			// Hover-based cursors
			if (state.hovered.rotationZone) {
				container.style.cursor = "grab";
				clearNodeCursor();
				return;
			}

			if (state.hovered.resizeHandleId) {
				const parsed = parseResizeHandleId(state.hovered.resizeHandleId);
				if (parsed) {
					const rotation = getNodeRotation?.(parsed.nodeId) ?? 0;
					const cursor =
						rotation !== 0
							? getRotatedResizeCursor(parsed.position, rotation)
							: RESIZE_CURSORS[parsed.position];
					container.style.cursor = cursor;

					// Also set on the node element
					if (canvas) {
						clearNodeCursor();
						const element = canvas.getNodeElement(parsed.nodeId);
						if (element) {
							element.style.cursor = cursor;
							lastCursorNodeId = parsed.nodeId;
						}
					}
					return;
				}
			}

			// Tool cursor: when a tool with cursorSvg is active
			if (toolCursor) {
				container.style.cursor = toolCursor;
				clearNodeCursor();
				return;
			}

			// Default: reset cursor
			if (!state.boxSelect) {
				container.style.cursor = "";
				clearNodeCursor();
			}
		},

		setToolCursor(cursor: string | null): void {
			toolCursor = cursor;
		},

		reset(): void {
			toolCursor = null;
			container.style.cursor = "";
			clearNodeCursor();
		},
	};
}
